/**
 * La pagina iniziale: una dashboard di widget che ogni persona sceglie,
 * ordina e dimensiona, e che ritrova uguale da qualunque dispositivo.
 *
 * Le regole stanno in src/lib/dashboard/ (catalogo, lettura della
 * disposizione, basi per ruolo), la grafica in src/components/dashboard/.
 *
 * Tre promesse verso chi la usa:
 * - niente si salva da solo: si salva con "Salva", e se si chiude la pagina
 *   con modifiche aperte il browser avvisa;
 * - una dashboard salvata che non si riesce a leggere non si sovrascrive:
 *   si vede la base del ruolo e la modifica resta spenta;
 * - utente e ruolo si leggono una volta qui e si passano ai widget, che non
 *   rifanno la lettura del profilo ognuno per conto suo.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { AlertTriangle, LayoutGrid, Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAggiornaDashboard } from '../hooks/useFonte';
import { useDisposizioneSalvata } from '../hooks/useDisposizioneSalvata';
import { useStore } from '../store/useStore';
import type { TipoWidget } from '../lib/dashboard/catalogo';
import {
  MAX_CARATTERI_LAYOUT,
  leggiLayout,
  modificaLayout,
  nuovoId,
  scriviLayout,
  stessaDisposizione,
  type AzioneLayout,
  type LayoutDashboard,
} from '../lib/dashboard/layout';
import { layoutPredefinito } from '../lib/dashboard/predefiniti';
import type { Json } from '../types/database.types';
import type { UserRole } from '../types/roles';
import { BarraModifica } from '../components/dashboard/BarraModifica';
import { CatalogoWidget } from '../components/dashboard/CatalogoWidget';
import { DatiDashboard } from '../components/dashboard/DatiDashboard';
import { GrigliaWidget } from '../components/dashboard/GrigliaWidget';
import { ImpostazioniWidget } from '../components/dashboard/ImpostazioniWidget';
import { CLASSI_GRIGLIA, CLASSI_TAGLIA } from '../components/dashboard/taglie';
import { Button } from '../components/ui/button';

function saluto(ora = new Date().getHours()): string {
  if (ora < 13) return 'Buongiorno';
  if (ora < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}

function GrigliaInCaricamento() {
  return (
    <div className={CLASSI_GRIGLIA} aria-label="Caricamento dashboard">
      {(['M', 'M', 'L', 'L'] as const).map((t, i) => (
        <div key={i} className={`${CLASSI_TAGLIA[t]} animate-pulse rounded-[1.75rem] bg-gradient-to-br from-neutral-100 to-neutral-50`} />
      ))}
    </div>
  );
}

function Avviso({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" /> <span>{children}</span>
    </div>
  );
}

function DashboardPersonale({ utenteId, ruolo, nome }: { utenteId: string; ruolo: UserRole; nome: string | null }) {
  const addNotification = useStore((s) => s.addNotification);
  const aggiorna = useAggiornaDashboard();
  const { stato, salva } = useDisposizioneSalvata(utenteId);
  const base = useMemo(() => layoutPredefinito(ruolo), [ruolo]);
  const lettura = useMemo(
    () => (stato.stato === 'pronto' ? leggiLayout(stato.salvato, ruolo, base) : null),
    [stato, ruolo, base],
  );

  const [bozza, setBozza] = useState<LayoutDashboard | null>(null);
  const [catalogoAperto, setCatalogoAperto] = useState(false);
  const [impostazioniDi, setImpostazioniDi] = useState<string | null>(null);
  const [inSalvataggio, setInSalvataggio] = useState(false);

  const contesto = useMemo(() => ({ utenteId, ruolo }), [utenteId, ruolo]);
  const dispatch = useCallback(
    (azione: AzioneLayout) => setBozza((b) => (b ? modificaLayout(b, azione, { ruolo, generaId: nuovoId }) : b)),
    [ruolo],
  );

  const mostrata = bozza ?? lettura?.layout ?? base;
  const modificabile = !!lettura && lettura.esito !== 'versione-futura';
  const modificato = !!bozza && !!lettura && !stessaDisposizione(bozza, lettura.layout);

  useEffect(() => {
    if (!modificato) return;
    const avvisa = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avvisa);
    return () => window.removeEventListener('beforeunload', avvisa);
  }, [modificato]);

  const personalizza = () => {
    if (lettura) setBozza(lettura.layout);
  };

  const aggiungi = (tipo: TipoWidget) => {
    dispatch({ tipo: 'aggiungi', widget: tipo });
    setCatalogoAperto(false);
  };

  const salvaBozza = async () => {
    if (!bozza || !lettura) return;
    // Uguale alla base e niente da conservare: si torna a seguire la base.
    const daSalvare = lettura.conservati.length === 0 && stessaDisposizione(bozza, base)
      ? null
      : scriviLayout(bozza, lettura.conservati);
    if (daSalvare && JSON.stringify(daSalvare).length > MAX_CARATTERI_LAYOUT) {
      addNotification({ type: 'error', title: 'Dashboard troppo grande', message: 'Togli qualche widget o accorcia le note, poi salva di nuovo.' });
      return;
    }
    setInSalvataggio(true);
    try {
      await salva(daSalvare as unknown as Json | null);
      setBozza(null);
      addNotification({ type: 'success', title: 'Dashboard salvata', message: 'La ritrovi così anche dagli altri dispositivi.' });
    } catch {
      addNotification({ type: 'error', title: 'Dashboard non salvata', message: 'Non sono riuscito a salvarla. Le modifiche restano qui: riprova tra poco.' });
    } finally {
      setInSalvataggio(false);
    }
  };

  const widgetImpostazioni = impostazioniDi && bozza ? bozza.widget.find((w) => w.id === impostazioniDi) ?? null : null;

  return (
    <div className="space-y-4 md:space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-sm font-medium capitalize text-neutral-400">{format(new Date(), 'EEEE d MMMM', { locale: it })}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-neutral-900 md:text-4xl">
            {saluto()}{nome ? `, ${nome.split(' ')[0]}` : ''}!
          </h1>
        </div>
        {!bozza && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={aggiorna}
              title="Rileggi i dati di tutti i widget"
              aria-label="Aggiorna i dati"
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-neutral-700 shadow-sm ring-1 ring-neutral-200 transition hover:rotate-90 hover:text-neutral-900"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={personalizza}
              disabled={!modificabile}
              title={modificabile ? 'Scegli, sposta e dimensiona i widget' : 'La modifica non è disponibile in questo momento'}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-neutral-900 px-5 text-sm font-semibold text-white shadow-lg shadow-neutral-900/20 transition hover:-translate-y-0.5 hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
            >
              <LayoutGrid className="h-4 w-4" /> Personalizza
            </button>
          </div>
        )}
      </div>

      {stato.stato === 'errore' && (
        <Avviso>Non riesco a leggere la tua dashboard salvata: vedi quella di partenza. La modifica torna disponibile quando la lettura riesce (ricarica la pagina).</Avviso>
      )}
      {lettura?.esito === 'versione-futura' && (
        <Avviso>Questa dashboard è stata salvata da una versione più nuova dell&apos;app: ricarica la pagina per vederla.</Avviso>
      )}

      {bozza && (
        <BarraModifica
          modificato={modificato}
          inSalvataggio={inSalvataggio}
          onAggiungi={() => setCatalogoAperto(true)}
          onRipristina={() => dispatch({ tipo: 'sostituisci', layout: base })}
          onAnnulla={() => setBozza(null)}
          onSalva={salvaBozza}
        />
      )}

      {stato.stato === 'caricamento' ? (
        <GrigliaInCaricamento />
      ) : mostrata.widget.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-gray-300 px-4 py-12 text-center">
          <p className="text-sm text-neutral-500">La tua dashboard è vuota.</p>
          <Button
            variant="outline"
            disabled={!modificabile}
            onClick={() => {
              if (!bozza) personalizza();
              setCatalogoAperto(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> Aggiungi widget
          </Button>
        </div>
      ) : (
        <GrigliaWidget layout={mostrata} inModifica={!!bozza} dispatch={dispatch} contesto={contesto} onImpostazioni={setImpostazioniDi} />
      )}

      {bozza && (
        <CatalogoWidget aperto={catalogoAperto} onChiudi={() => setCatalogoAperto(false)} ruolo={ruolo} layout={bozza} onAggiungi={aggiungi} />
      )}
      <ImpostazioniWidget
        widget={widgetImpostazioni}
        ruolo={ruolo}
        onChiudi={() => setImpostazioniDi(null)}
        onApplica={(id, impostazioni) => dispatch({ tipo: 'impostazioni', id, impostazioni })}
      />
    </div>
  );
}

export default function Dashboard() {
  const { user, profile, loading } = useAuth();

  // Si aspetta il profilo: senza, l'app tratterebbe chiunque da "lettore".
  if (loading || !user || !profile) {
    return (
      <div className="space-y-4 md:space-y-5">
        <div className="h-9 w-64 animate-pulse rounded bg-gray-100" />
        <GrigliaInCaricamento />
      </div>
    );
  }

  return (
    <DatiDashboard>
      <DashboardPersonale utenteId={user.id} ruolo={profile.role as UserRole} nome={profile.full_name} />
    </DatiDashboard>
  );
}
